"use client";

import {
    SURVEY_ANALYTICS_QUERY,
    SURVEY_DETAIL_QUERY,
    SURVEY_RESPONSES_QUERY,
} from "@/graphql/queries/surveyQueries";
import { useQuery } from "@apollo/client";
import {
    ArrowLeftIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

interface OptionBreakdown {
    optionId: string;
    text: string;
    count: number;
    percentage: number;
}

interface QuestionAnalytics {
    questionId: string;
    text: string;
    questionType: string;
    totalAnswers: number;
    optionBreakdown: OptionBreakdown[];
    textResponses: string[];
    numberAverage: number | null;
    numberMin: number | null;
    numberMax: number | null;
}

interface Analytics {
    totalTargeted: number;
    totalResponses: number;
    totalViews: number;
    completionRate: number;
    questions: QuestionAnalytics[];
}

const CHART_COLORS = [
    "#facc15",
    "#38bdf8",
    "#a78bfa",
    "#34d399",
    "#fb923c",
    "#f87171",
    "#e879f9",
    "#22d3ee",
];

interface ResponseAnswer {
    id: string;
    question: {
        id: string;
        text: string;
        questionType: string;
        order: number;
    };
    textAnswer: string;
    numberAnswer: number | null;
    selectedOptions: { id: string; text: string }[];
}

interface SurveyResponseItem {
    id: string;
    username: string;
    completedAt: string;
    vpAwarded: boolean;
    answers: ResponseAnswer[];
}

type TabKey = "analytics" | "responses";

const RESPONSES_PER_PAGE = 20;

export default function SurveyAnalyticsPage() {
    const { id } = useParams<{ id: string }>();
    const [activeTab, setActiveTab] = useState<TabKey>("analytics");
    const [expandedResponseId, setExpandedResponseId] = useState<string | null>(
        null,
    );
    const [currentPage, setCurrentPage] = useState(1);

    const { data: surveyData, loading: surveyLoading } = useQuery(
        SURVEY_DETAIL_QUERY,
        {
            variables: { id },
        },
    );

    const { data: analyticsData, loading: analyticsLoading } = useQuery(
        SURVEY_ANALYTICS_QUERY,
        {
            variables: { id },
        },
    );

    const { data: responsesData, loading: responsesLoading } = useQuery(
        SURVEY_RESPONSES_QUERY,
        {
            variables: { id },
        },
    );

    if (surveyLoading || analyticsLoading || responsesLoading) {
        return (
            <div className="flex min-h-50 items-center justify-center">
                <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
            </div>
        );
    }

    const survey = surveyData?.survey;
    const analytics: Analytics | null = analyticsData?.surveyAnalytics ?? null;
    const responses: SurveyResponseItem[] =
        responsesData?.surveyResponses ?? [];

    const totalPages = Math.max(
        1,
        Math.ceil(responses.length / RESPONSES_PER_PAGE),
    );
    const paginatedResponses = responses.slice(
        (currentPage - 1) * RESPONSES_PER_PAGE,
        currentPage * RESPONSES_PER_PAGE,
    );

    if (!survey || !analytics) {
        return (
            <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                Survey or analytics data not found.
            </div>
        );
    }

    const handleExport = async () => {
        const baseUrl = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL as string;
        const response = await fetch(
            `${baseUrl}/longhouse-api/surveys/${id}/export`,
            { credentials: "include" },
        );
        if (!response.ok) return;
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${survey.title} - Responses.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const toggleResponse = (responseId: string) => {
        setExpandedResponseId((prev) =>
            prev === responseId ? null : responseId,
        );
    };

    const formatAnswer = (answer: ResponseAnswer): string => {
        const { questionType } = answer.question;
        if (questionType === "text_short" || questionType === "text_long") {
            return answer.textAnswer || "—";
        }
        if (questionType === "number" || questionType === "rating") {
            return answer.numberAnswer !== null
                ? String(answer.numberAnswer)
                : "—";
        }
        if (questionType === "multiple_choice" || questionType === "checkbox") {
            return answer.selectedOptions.length > 0
                ? answer.selectedOptions.map((o) => o.text).join(", ")
                : "—";
        }
        return answer.textAnswer || "—";
    };

    return (
        <div className="mx-auto mt-4 w-full space-y-6 px-4">
            {/* Page Header */}
            <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <Link
                    to="/longhouse/surveys"
                    className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Surveys
                </Link>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {survey.title}
                </h1>
                <p className="mt-2 text-slate-600 dark:text-slate-400">
                    Survey Analytics &amp; Responses
                </p>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <StatCard label="Targeted" value={analytics.totalTargeted} />
                <StatCard label="Responses" value={analytics.totalResponses} />
                <StatCard label="Views" value={analytics.totalViews} />
                <StatCard
                    label="Completion"
                    value={`${analytics.completionRate.toFixed(1)}%`}
                />
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-4 border-b border-slate-300 dark:border-slate-700">
                <button
                    onClick={() => setActiveTab("analytics")}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === "analytics"
                            ? "border-brand text-brand border-b-2"
                            : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    }`}
                >
                    Analytics
                </button>
                <button
                    onClick={() => setActiveTab("responses")}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === "responses"
                            ? "border-brand text-brand border-b-2"
                            : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    }`}
                >
                    Responses ({responses.length})
                </button>
            </div>

            {/* Analytics Tab */}
            {activeTab === "analytics" && (
                <div className="space-y-6">
                    {analytics.questions.map((q, i) => (
                        <div
                            key={q.questionId}
                            className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                        >
                            <h3 className="mb-1 font-semibold text-slate-900 dark:text-slate-100">
                                Q{i + 1}: {q.text}
                            </h3>
                            <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
                                {q.totalAnswers} answers &middot;{" "}
                                {q.questionType.replace("_", " ")}
                            </p>

                            {(q.questionType === "multiple_choice" ||
                                q.questionType === "checkbox") &&
                                q.optionBreakdown.length > 0 && (
                                    <div className="space-y-4">
                                        <ResponsiveContainer
                                            width="100%"
                                            height={Math.max(
                                                200,
                                                q.optionBreakdown.length * 50,
                                            )}
                                        >
                                            <BarChart
                                                data={q.optionBreakdown}
                                                layout="vertical"
                                                margin={{
                                                    top: 5,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    stroke="#334155"
                                                />
                                                <XAxis
                                                    type="number"
                                                    stroke="#94a3b8"
                                                    allowDecimals={false}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="text"
                                                    width={160}
                                                    tick={{
                                                        fill: "#cbd5e1",
                                                        fontSize: 13,
                                                    }}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor:
                                                            "#1e293b",
                                                        border: "1px solid #334155",
                                                        borderRadius: "8px",
                                                        color: "#e2e8f0",
                                                    }}
                                                    labelStyle={{
                                                        color: "#f1f5f9",
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="count"
                                                    radius={[0, 4, 4, 0]}
                                                >
                                                    {q.optionBreakdown.map(
                                                        (_, idx) => (
                                                            <Cell
                                                                key={idx}
                                                                fill={
                                                                    CHART_COLORS[
                                                                        idx %
                                                                            CHART_COLORS.length
                                                                    ]
                                                                }
                                                            />
                                                        ),
                                                    )}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>

                                        <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-3">
                                            {q.optionBreakdown.map((opt) => (
                                                <div
                                                    key={opt.optionId}
                                                    className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
                                                >
                                                    <span className="text-slate-700 dark:text-slate-300">
                                                        {opt.text}
                                                    </span>
                                                    <span className="text-slate-500 dark:text-slate-400">
                                                        {opt.count} (
                                                        {opt.percentage.toFixed(
                                                            1,
                                                        )}
                                                        %)
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            {q.questionType === "rating" &&
                                q.optionBreakdown.length > 0 && (
                                    <div>
                                        <ResponsiveContainer
                                            width="100%"
                                            height={200}
                                        >
                                            <BarChart data={q.optionBreakdown}>
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    stroke="#334155"
                                                />
                                                <XAxis
                                                    dataKey="text"
                                                    stroke="#94a3b8"
                                                />
                                                <YAxis
                                                    stroke="#94a3b8"
                                                    allowDecimals={false}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor:
                                                            "#1e293b",
                                                        border: "1px solid #334155",
                                                        borderRadius: "8px",
                                                        color: "#e2e8f0",
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="count"
                                                    fill="#facc15"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                        {q.numberAverage !== null && (
                                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                                Average:{" "}
                                                {q.numberAverage.toFixed(1)}
                                            </p>
                                        )}
                                    </div>
                                )}

                            {q.questionType === "number" && (
                                <div className="grid grid-cols-3 gap-4">
                                    {q.numberAverage !== null && (
                                        <StatCard
                                            label="Average"
                                            value={q.numberAverage.toFixed(1)}
                                            small
                                        />
                                    )}
                                    {q.numberMin !== null && (
                                        <StatCard
                                            label="Min"
                                            value={q.numberMin}
                                            small
                                        />
                                    )}
                                    {q.numberMax !== null && (
                                        <StatCard
                                            label="Max"
                                            value={q.numberMax}
                                            small
                                        />
                                    )}
                                </div>
                            )}

                            {(q.questionType === "text_short" ||
                                q.questionType === "text_long") &&
                                q.textResponses.length > 0 && (
                                    <div className="max-h-60 space-y-2 overflow-y-auto">
                                        {q.textResponses.map((text, ti) => (
                                            <div
                                                key={ti}
                                                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                            >
                                                {text}
                                            </div>
                                        ))}
                                    </div>
                                )}
                        </div>
                    ))}
                </div>
            )}

            {/* Responses Tab */}
            {activeTab === "responses" && (
                <div className="space-y-4">
                    {/* Export button */}
                    <div className="flex justify-end">
                        <button
                            onClick={handleExport}
                            className="bg-brand hover:bg-brand/90 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
                        >
                            Export to Excel
                        </button>
                    </div>

                    {responses.length === 0 ? (
                        <div className="rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                            <p className="text-slate-500 dark:text-slate-400">
                                No responses yet.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-2xl border border-slate-300 shadow-lg dark:border-slate-700">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
                                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-600 uppercase dark:text-slate-400">
                                            #
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-600 uppercase dark:text-slate-400">
                                            Respondent
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-600 uppercase dark:text-slate-400">
                                            Completed
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-600 uppercase dark:text-slate-400">
                                            VP Awarded
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium tracking-wider text-slate-600 uppercase dark:text-slate-400">
                                            Details
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedResponses.map((resp, idx) => (
                                        <ResponseRow
                                            key={resp.id}
                                            response={resp}
                                            index={
                                                (currentPage - 1) *
                                                    RESPONSES_PER_PAGE +
                                                idx +
                                                1
                                            }
                                            isExpanded={
                                                expandedResponseId === resp.id
                                            }
                                            onToggle={() =>
                                                toggleResponse(resp.id)
                                            }
                                            formatAnswer={formatAnswer}
                                        />
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between border-t border-slate-300 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                                    <p className="text-sm text-slate-600 dark:text-slate-400">
                                        Showing{" "}
                                        {(currentPage - 1) *
                                            RESPONSES_PER_PAGE +
                                            1}
                                        –
                                        {Math.min(
                                            currentPage * RESPONSES_PER_PAGE,
                                            responses.length,
                                        )}{" "}
                                        of {responses.length} responses
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setCurrentPage((p) =>
                                                    Math.max(1, p - 1),
                                                );
                                                setExpandedResponseId(null);
                                            }}
                                            disabled={currentPage === 1}
                                            className="rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => {
                                                setCurrentPage((p) =>
                                                    Math.min(totalPages, p + 1),
                                                );
                                                setExpandedResponseId(null);
                                            }}
                                            disabled={
                                                currentPage === totalPages
                                            }
                                            className="rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function StatCard({
    label,
    value,
    small,
}: {
    label: string;
    value: string | number;
    small?: boolean;
}) {
    return (
        <div className="rounded-2xl border border-slate-300 bg-white p-4 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
            <p
                className={`font-bold text-slate-900 dark:text-slate-100 ${small ? "text-lg" : "text-2xl"}`}
            >
                {value}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400">
                {label}
            </p>
        </div>
    );
}

function ResponseRow({
    response,
    index,
    isExpanded,
    onToggle,
    formatAnswer,
}: {
    response: SurveyResponseItem;
    index: number;
    isExpanded: boolean;
    onToggle: () => void;
    formatAnswer: (answer: ResponseAnswer) => string;
}) {
    const sortedAnswers = [...response.answers].sort(
        (a, b) => a.question.order - b.question.order,
    );

    return (
        <>
            <tr
                onClick={onToggle}
                className="cursor-pointer border-b border-slate-200 bg-white transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
            >
                <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                    {index}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                    {response.username}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                    {new Date(response.completedAt).toLocaleDateString(
                        undefined,
                        {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        },
                    )}
                </td>
                <td className="px-4 py-3 text-sm">
                    <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            response.vpAwarded
                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"
                                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                        }`}
                    >
                        {response.vpAwarded ? "Yes" : "No"}
                    </span>
                </td>
                <td className="px-4 py-3 text-right">
                    {isExpanded ? (
                        <ChevronUpIcon className="ml-auto h-5 w-5 text-slate-400" />
                    ) : (
                        <ChevronDownIcon className="ml-auto h-5 w-5 text-slate-400" />
                    )}
                </td>
            </tr>
            {isExpanded && (
                <tr className="border-b border-slate-200 dark:border-slate-700">
                    <td
                        colSpan={5}
                        className="bg-slate-50 px-4 py-4 dark:bg-slate-800/50"
                    >
                        <div className="space-y-3">
                            {sortedAnswers.map((answer) => (
                                <div
                                    key={answer.id}
                                    className="rounded-lg border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
                                >
                                    <p className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
                                        {answer.question.text}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                                        {formatAnswer(answer)}
                                    </p>
                                </div>
                            ))}
                            {sortedAnswers.length === 0 && (
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    No answers recorded.
                                </p>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
